import { Component } from '@theme/component';

/**
 * Disclaimer toast component that shows once per session.
 * Displays as a subtle notification in the bottom right corner.
 * Uses sessionStorage to remember acceptance within the current browser session.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} toast - The toast container element.
 *
 * @extends Component<Refs>
 */
class DisclaimerPopupComponent extends Component {
  requiredRefs = ['toast'];

  /** @type {string} */
  #storageKey = 'disclaimer_accepted';

  connectedCallback() {
    super.connectedCallback();

    this.#storageKey = this.dataset.storageKey || 'disclaimer_accepted';

    // Check if already accepted this session
    if (!this.#hasAccepted()) {
      // Delay slightly to ensure page is ready and give user time to see the page first
      setTimeout(() => {
        this.showToast();
      }, 800);
    }
  }

  /**
   * Check if disclaimer has been accepted this session.
   * @returns {boolean}
   */
  #hasAccepted() {
    try {
      return sessionStorage.getItem(this.#storageKey) === 'true';
    } catch (e) {
      // sessionStorage might be blocked
      return false;
    }
  }

  /**
   * Save acceptance to sessionStorage.
   */
  #saveAcceptance() {
    try {
      sessionStorage.setItem(this.#storageKey, 'true');
    } catch (e) {
      // sessionStorage might be blocked, continue anyway
    }
  }

  /**
   * Shows the toast notification.
   */
  showToast() {
    const { toast } = this.refs;

    if (!toast.hidden) return;

    toast.hidden = false;

    // Trigger animation after a brief delay to ensure the element is rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.classList.add('is-visible');
      });
    });
  }

  /**
   * Closes the toast notification.
   */
  closeToast = async () => {
    const { toast } = this.refs;

    if (toast.hidden) return;

    toast.classList.remove('is-visible');
    toast.classList.add('is-closing');

    // Wait for transition to complete
    await new Promise((resolve) => setTimeout(resolve, 300));

    toast.classList.remove('is-closing');
    toast.hidden = true;
  };

  /**
   * Handle accept button click - saves acceptance and closes toast.
   */
  acceptDisclaimer() {
    this.#saveAcceptance();
    this.closeToast();
  }
}

if (!customElements.get('disclaimer-popup-component')) {
  customElements.define('disclaimer-popup-component', DisclaimerPopupComponent);
}
